import {creates, elementFactory, hoistCmp, HoistProps} from '@xh/hoist/core';
import {DealsModel} from './DealsModel';
import {panel} from '@xh/hoist/desktop/cmp/panel';
import {toolbar} from '@xh/hoist/desktop/cmp/toolbar';
import {div, filler, hbox, hspacer, img, vbox, vframe} from '@xh/hoist/cmp/layout';
import {ReactElement} from 'react';
import './Deals.scss';
import {button} from '@xh/hoist/desktop/cmp/button';
import {Icon} from '@xh/hoist/icon';
import {dealDialog} from './DealDialog';
import {CardList} from '@blueprintjs/core';
import {bpCard, tag} from '@xh/hoist/kit/blueprint';
import {moneyColorFormatter} from '../../../cmp/Formaters';
import {fmtDate, fmtPercent} from '@xh/hoist/format';
import {random, sumBy} from 'lodash';
import {RealEstateDeal, RealEstateStatusEnum} from '../../../data/DataTypes';
import {cashFlowsDialog} from './CashFlowsDialog';
import {textInput} from '@xh/hoist/desktop/cmp/input';

interface DealProps extends HoistProps<DealsModel> {
    deal: RealEstateDeal;
}

export const deals = hoistCmp.factory({
    model: creates(DealsModel),
    className: 'deals',
    render({model, className}) {
        return panel({
            tbar: tbar(),
            scrollable: true,
            contentBoxProps: {className},
            items: [
                ...model.deals.map(d => dealContent({key: d.id, deal: d})),
                dealDialog({model: model.dealEditModel}),
                cashFlowsDialog({model: model.dealCfModel})
            ],
            mask: model.apiTask
        });
    }
});

const tbar = hoistCmp.factory<DealsModel>(({model}) =>
    toolbar({
        compact: true,
        items: [
            button({
                icon: Icon.add(),
                text: 'Add',
                intent: 'success',
                onClick: () => model.addNew()
            }),
            button({
                icon: Icon.refresh(),
                text: 'Refresh',
                intent: 'primary',
                onClick: () => model.refreshAsync()
            }),
            textInput({
                bind: 'searchText',
                placeholder: 'Search...',
                commitOnChange: true,
                enableClear: true
            })
        ]
    })
);

const dealContent = hoistCmp.factory<DealProps>({
    render({model, deal}) {
        const className = 'deals__deal';
        return hbox({
            key: deal.id,
            className,
            items: [picture({deal}), description(deal), financials({deal})],
            onDoubleClick: () => model.edit(deal)
        });
    }
});

const picture = hoistCmp.factory<DealProps>(({model, deal}) => {
    return img({className: 'deals__deal__picture', src: model.getImageUrl(deal)});
});

const description = (deal: RealEstateDeal): ReactElement => {
    const classBase = 'deals__deal__description';
    return vframe({
        className: classBase,
        items: [
            div({className: classBase + '__name', item: deal.name}),
            div({className: classBase + '__sponsor', item: deal.sponsor}),
            div({className: classBase + '__description', item: deal.description}),
            hbox(
                vbox({
                    className: classBase + '__pairs',
                    items: [div({className: classBase + '__header', item: 'Asset Type'}), div({className: classBase + '__value', item: deal.type})]
                }),
                vbox({
                    className: classBase + '__pairs',
                    items: [div({className: classBase + '__header', item: 'Objective'}), div({className: classBase + '__value', item: deal.objective})]
                })
            ),
            hbox(
                vbox({
                    className: classBase + '__pairs',
                    items: [div({className: classBase + '__header', item: 'Tax Reporting'}), div({className: classBase + '__value', item: deal.taxReporting})]
                }),
                vbox({
                    className: classBase + '__pairs',
                    items: [div({className: classBase + '__header', item: 'Structure'}), div({className: classBase + '__value', item: deal.structure})]
                })
            )
        ]
    });
};

const cardList = elementFactory(CardList);

const financials = hoistCmp.factory<DealProps>({
    render({model, deal}) {
        const classBase = 'deals__deal__financials';
        return div({
            className: classBase,
            item: cardList({
                compact: true,
                bordered: false,
                items: [
                    bpCard({
                        item: hbox({
                            className: classBase + '__card',
                            items: ['Inception', filler(), fmtDate(deal.inceptionDate), hspacer(25)]
                        })
                    }),
                    bpCard({
                        item: hbox({
                            className: classBase + '__card',
                            items: [
                                'Status',
                                filler(),
                                tag({
                                    item: deal.status,
                                    intent:
                                        deal.status === RealEstateStatusEnum.Active
                                            ? 'primary'
                                            : sumBy(deal.cashFlows, 'amount') < deal.capital
                                              ? 'danger'
                                              : 'success'
                                }),
                                hspacer(25)
                            ]
                        })
                    }),
                    bpCard({
                        item: hbox({
                            className: classBase + '__card',
                            items: ['Est. Maturity', filler(), deal.loanMaturityDate ? fmtDate(deal.loanMaturityDate) : '--', hspacer(25)]
                        })
                    }),
                    bpCard({
                        item: hbox({
                            className: classBase + '__card',
                            items: ['LTV', filler(), deal.ltv ? fmtPercent(deal.ltv) : '--', hspacer(25)]
                        })
                    }),
                    bpCard({
                        item: hbox({
                            className: classBase + '__card',
                            items: ['Capital', filler(), moneyColorFormatter(-deal.capital), hspacer(25)]
                        })
                    }),
                    bpCard({
                        item: hbox({
                            className: classBase + '__card',
                            items: [
                                'Distributions',
                                filler(),
                                moneyColorFormatter(sumBy(deal.cashFlows, 'amount')),
                                hspacer(5),
                                button({
                                    icon: Icon.eye(),
                                    className: classBase + '__button',
                                    tooltip: 'view',
                                    intent: 'primary',
                                    onClick: () => model.showCashFlows(deal)
                                })
                            ]
                        })
                    }),
                    bpCard({
                        item: hbox({
                            className: classBase + '__card',
                            items: [
                                'Documents',
                                filler(),
                                tag({item: random(1, 10, false)}),
                                hspacer(5),
                                button({
                                    icon: Icon.eye(),
                                    className: classBase + '__button',
                                    tooltip: 'view',
                                    intent: 'primary',
                                    onClick: () => model.showDocuments(deal)
                                })
                            ]
                        })
                    })
                ]
            })
        });
    }
});
